import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, FileText, ClipboardList, ShoppingCart, Package,
  Receipt, BarChart3, FolderOpen, Settings as SettingsIcon, Menu, X, ShieldCheck,
  Truck, Warehouse, Wrench, Upload, ScanLine, Store, Inbox, Landmark, Cable,
  CircleHelp, Car, CircleDot, BellRing,
} from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import Topbar from "./Topbar";
import CommandPalette from "@/components/common/CommandPalette";
import HelpDrawer from "@/components/help/HelpDrawer";
import BrandMark from "@/components/common/BrandMark";

const GROUPS = [
  { key: "overview", items: [
    { to: "/", key: "dashboard", icon: LayoutDashboard, perm: "dashboard:read", end: true },
  ] },
  { key: "sales", items: [
    { to: "/customers", key: "customers", icon: Users, perm: "customers:read" },
    { to: "/vehicles", key: "vehicles", icon: Car, perm: "customers:read", module: "automotive_workshop", capability: "vehicles" },
    { to: "/quotes", key: "quotes", icon: FileText, perm: "quotes:read" },
    { to: "/orders", key: "orders", icon: ClipboardList, perm: "orders:read" },
    { to: "/jobs", key: "jobs", icon: Wrench, perm: "jobs:read" },
  ] },
  { key: "procurement", items: [
    { to: "/suppliers", key: "suppliers", icon: Truck, perm: "suppliers:read" },
    { to: "/purchasing", key: "purchasing", icon: ShoppingCart, perm: "purchasing:read" },
    { to: "/buying-group", key: "buying_group", icon: Cable, perm: "purchasing:read", module: "buying_group_connector" },
  ] },
  { key: "catalog", items: [
    { to: "/products", key: "products", icon: Package, perm: "products:read" },
    { to: "/inventory", key: "inventory", icon: Warehouse, perm: "inventory:read" },
    { to: "/scan", key: "scan", icon: ScanLine, perm: "inventory:read", module: "inventory_scan" },
    { to: "/tire-storage", key: "tire_storage", icon: CircleDot, perm: "inventory:read", module: "automotive_workshop", capability: "tire_storage" },
    { to: "/imports", key: "imports", icon: Upload, perm: "products:write" },
  ] },
  { key: "finance", items: [
    { to: "/finance/einvoices", key: "einvoices", icon: Receipt, perm: "einvoice:view", module: "einvoice_datev" },
    { to: "/finance/dunning", key: "dunning", icon: BellRing, perm: "einvoice:view", module: "einvoice_datev" },
    { to: "/finance/inbound", key: "inbound", icon: Inbox, perm: "einvoice:inbound", module: "einvoice_datev" },
    { to: "/finance/datev", key: "datev", icon: FolderOpen, perm: "datev:export", module: "einvoice_datev" },
    { to: "/finance/banking", key: "banking", icon: Landmark, perm: "banking:view", module: "banking" },
  ] },
  { key: "admin", items: [
    { to: "/shop-admin", key: "shop", icon: Store, perm: "shop:read", module: "online_shop" },
    { to: "/settings", key: "settings", icon: SettingsIcon, perm: "settings:read" },
    { to: "/help", key: "help", icon: CircleHelp },
  ] },
];

const linkClass = ({ isActive }) =>
  cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    isActive
      ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
  );

function SidebarContent({ onNavigate }) {
  const { t } = useI18n();
  const { can, canPlatform, modules, tenantRole, businessCapabilities } = useAuth();
  const technician = tenantRole === "field_service";
  const showPos = !technician && modules?.includes("pos") && can("pos:use");
  return (
    <nav className="flex flex-col gap-4 p-3" aria-label={t("nav.dashboard")}>
      {showPos && (
        <div className="flex flex-col gap-1">
          <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{t("pos.station")}</p>
          <a href="/pos/station" target="_blank" rel="noopener noreferrer" onClick={onNavigate} data-testid="nav-pos-station"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40">
            <Store className="h-4 w-4 shrink-0" /><span>{t("pos.station")}</span>
          </a>
        </div>
      )}
      {GROUPS.map((g) => {
        const items = g.items.filter((n) =>
          (!technician || n.key === "jobs" || n.key === "help") &&
          (!n.perm || can(n.perm)) &&
          (!n.module || modules?.includes(n.module)) &&
          (!n.capability || businessCapabilities?.[n.capability] !== false)
        );
        if (items.length === 0) return null;
        return (
          <div key={g.key} className="flex flex-col gap-1">
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500" data-testid={`nav-group-${g.key}`}>{t(`nav.group_${technician ? "service" : g.key}`)}</p>
            {items.map((n) => (
              <NavLink key={n.key} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.key}`} className={linkClass}>
                <n.icon className="h-4 w-4 shrink-0" />
                <span>{t(`nav.${n.key}`)}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
      {canPlatform("tenants:read") && (
        <div className="flex flex-col gap-1">
          <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{t("nav.platform")}</p>
          <NavLink to="/platform" onClick={onNavigate} data-testid="nav-platform" className={linkClass}>
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span>{t("nav.platform")}</span>
          </NavLink>
        </div>
      )}
    </nav>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { t } = useI18n();
  const { tenantRole } = useAuth();
  const navigate = useNavigate();

function SupportBanner() {
  const [grants, setGrants] = useState([]);
  useEffect(() => {
    let alive = true;
    const check = () => api.get("/support-access/active").then(({ data }) => { if (alive) setGrants(data.active ? data.grants : []); }).catch(() => {});
    check();
    const id = setInterval(check, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (grants.length === 0) return null;
  return (
    <div
      role="status"
      className="sticky top-16 z-10 ml-auto mr-4 mt-2 flex w-[10cm] max-w-[calc(100%_-_2rem)] items-start gap-2 rounded-lg border border-amber-400 bg-amber-400 px-3 py-2 text-left text-sm font-semibold leading-snug text-amber-950 shadow-md dark:border-amber-500 dark:bg-amber-500 sm:mr-6 lg:mr-8"
      data-testid="support-access-banner"
    >
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        Mayr-Technologies Support has active read-only access ({grants[0].reason}). Contact: {grants[0].requester_email}
      </span>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:flex">
        <button
          onClick={() => navigate("/")}
          className="flex h-16 items-center gap-2 border-b border-slate-200 px-5 dark:border-slate-800"
          data-testid="brand-home"
        >
          <BrandMark className="h-8 w-8" />
          <span className="font-heading text-base font-bold tracking-tight">Mayr-Technologies</span>
        </button>
        <div className="flex-1 overflow-y-auto">
          <SidebarContent />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-slate-900" data-testid="mobile-sidebar">
            <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5 dark:border-slate-800">
              <span className="font-heading text-base font-bold">Mayr-Technologies</span>
              <button onClick={() => setMobileOpen(false)} data-testid="close-mobile-nav" aria-label={t("common.close")}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </div>
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <Topbar
          onOpenMobile={() => setMobileOpen(true)}
          onOpenSearch={tenantRole === "field_service" ? null : () => setSearchOpen(true)}
          onOpenHelp={() => setHelpOpen(true)}
        />
        <SupportBanner />
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      {tenantRole !== "field_service" && <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />}
      <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}
